import { mapGetters } from 'vuex'

import _ from 'lodash'

import * as fieldComponents from '@/advancedSearchFieldTypeComponents'

import { mapFromId } from '_/services/fieldTypes'

import { store } from '_/vuex/store'

import { isRelationFieldType } from '_/helpers/fieldHelper'

const photonConfig = require('~/config/config.json')

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    fields: {
      type: Array,
      required: true,
    },
    advancedSearchContext: {
      type: Boolean,
      default: true,
    },
    moduleTableName: {
      type: String,
      required: true,
    },
    relatedTableNames: {
      type: Array,
      default: () => [],
    },
    vuexModuleName: {
      type: String,
      default: 'advancedSearch',
    },
  },

  /**
   * Define the components
   *
   * @type  {Object}
   */
  components: {
    ...fieldComponents,
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {
      /**
       * Selected search filters
       *
       * @type  {Array}
       */
      filters: [],

      /**
       * Used to refresh fields
       *
       * @type  {string}
       */
      refreshFields: null,

      /**
       * Used to refresh settings fields
       *
       * @type  {string}
       */
      refreshAddFilter: null,
    }
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      admin: 'admin/admin',
    }),

    /**
     * Advanced Search module getter
     *
     * @return  {object}
     */
    advancedSearch() {
      const getterName = `${this.vuexModuleName}/${this.vuexModuleName}`
      return store.getters[getterName]
    },

    /**
     * Adds the created at and updated at filter options
     *
     * @return  {array}
     */
    augmentedFields() {
      return [
        ...this.fields,
        {
          defaultValue: null,
          disabled: true,
          flattenToOptgroups: false,
          hidden: false,
          id: 123456,
          isActiveEntryFilter: undefined,
          isSystem: true,
          label: 'Entry (Anchor text)',
          lazyLoading: false,
          multiple: false,
          mutation: 'admin/UPDATE_MODULE_ENTRY_FIELD',
          name: 'anchor_text',
          presentAsListed: false,
          presentAsReference: false,
          relatedEntry: undefined,
          relatedTableName: undefined,
          required: null,
          tooltip: 'Entry title.',
          value: undefined,
          vueComponent: 'InputText',
        },
        {
          defaultValue: null,
          disabled: true,
          flattenToOptgroups: false,
          hidden: false,
          id: 123,
          isActiveEntryFilter: undefined,
          isSystem: true,
          label: 'Created At',
          lazyLoading: false,
          multiple: false,
          mutation: 'admin/UPDATE_MODULE_ENTRY_FIELD',
          name: 'created_at',
          presentAsListed: false,
          presentAsReference: false,
          relatedEntry: undefined,
          relatedTableName: undefined,
          required: null,
          tooltip: 'Account creation date and time.',
          value: undefined,
          vueComponent: 'DateTime',
        },
        {
          canCreateSearchChoice: false,
          defaultValue: null,
          disabled: true,
          flattenToOptgroups: false,
          hidden: false,
          id: 456,
          isActiveEntryFilter: undefined,
          isSystem: true,
          label: 'Updated At',
          lazyLoading: false,
          multiple: false,
          mutation: 'admin/UPDATE_MODULE_ENTRY_FIELD',
          name: 'updated_at',
          presentAsListed: false,
          presentAsReference: false,
          relatedEntry: undefined,
          relatedTableName: undefined,
          required: null,
          tooltip: 'Account last update date and time.',
          value: undefined,
          vueComponent: 'DateTime',
        },
      ]
    },

    /**
     * Returns a list of available filters
     *
     * @return  {array}
     */
    availableFilters() {
      return this.augmentedFields
        .filter(field => {
          const component = _.find(mapFromId, { component: field.vueComponent })

          return (
            component.searchable &&
            !field.hidden &&
            _.findIndex(this.filters, { id: parseInt(field.id) }) === -1
          )
        })
        .map(field => {
          return {
            id: field.id,
            text: field.moduleName
              ? `${field.moduleName} > ${field.label}`
              : field.label,
          }
        })
    },

    /**
     * Retrieves the parameter operator options from config file
     *
     * @return  {array}
     */
    parameterOperatorOptions() {
      return photonConfig.availableAdvancedSearchOperatorOptions
    },
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    /**
     * Maps the appropriate action
     *
     * @param   {object}  payload
     * @return  {promise}
     */
    clearAdvancedSearchFilters(payload) {
      return store.dispatch(
        `${this.vuexModuleName}/clearAdvancedSearchFilters`,
        payload
      )
    },

    /**
     * Maps the appropriate action
     *
     * @param   {object}  filterParameters
     * @return  {promise}
     */
    filterModuleEntries(filterParameters) {
      return store.dispatch(
        `${this.vuexModuleName}/filterModuleEntries`,
        filterParameters
      )
    },

    /**
     * Adds a filter
     *
     * @param  {void}  options.value
     */
    addFilter({ value }) {
      let field = _.find(this.augmentedFields, { id: parseInt(value) })

      field['queryOperator'] = 'and'

      field['multiSelectQueryOperator'] = 'or'

      field['settingsMode'] = false

      this.filters.push(field)

      this.updateRefreshAddFilter()
    },

    /**
     * Checks if field type is a relation
     *
     * @param   {string}  fieldType
     * @return  {Boolean}
     */
    isRelationFieldType(fieldType) {
      return isRelationFieldType(fieldType)
    },

    /**
     * Removes a filter
     *
     * @param  {void}  options.value
     */
    removeFilter(id) {
      const index = _.findIndex(this.filters, { id: parseInt(id) })

      this.filters.splice(index, 1)

      this.updateRefreshAddFilter()
    },

    /**
     * Submits the search form
     *
     * @return  {void}
     */
    submitSearch() {
      const payload = this.advancedSearch.payload

      this.filterModuleEntries({
        moduleTableName:
          this.relatedTableNames.length > 1
            ? this.relatedTableNames
            : this.moduleTableName,
        payload,
      })
    },

    /**
     * Toggles the resetRefreshFields data property in order to force refresh of the RefreshFields component
     *
     * @return  {void}
     */
    toggleAddRefreshFields() {
      this.refreshFields = moment().valueOf()
    },

    /**
     * Toggles the resetRefreshFields data property in order to force refresh of the RefreshFields component
     *
     * @return  {void}
     */
    updateRefreshAddFilter() {
      this.refreshAddFilter = moment().valueOf()
    },

    /**
     * Toggles the settings mode
     *
     * @return  {void}
     */
    toggleSettingsMode(index) {
      let filter = this.filters[index]

      filter.settingsMode = !filter.settingsMode

      this.$set(this.filters, index, filter)
    },

    /**
     * Updates the query operator
     *
     * @param   {string}  options.name
     * @param   {string}  options.queryOperator
     * @return  {void}
     */
    dispatchUpdateQueryOperator({ name, queryOperator }) {
      if (queryOperator === 'or') {
        return store.dispatch(
          `${this.advancedSearch.advancedSearchName}/updateFilterValue`,
          {
            fieldName: `filter:${name}:or`,
            value: 1,
          }
        )
      }

      store.dispatch(`${this.advancedSearch.advancedSearchName}/removeFilter`, {
        fieldName: `filter.${name}.or`,
      })
    },

    /**
     * Fired on translations dropdown change event
     *
     * @param   {string}  options.id
     * @param   {array}  options.value
     * @return  {void}
     */
    updateQueryOperator({ id, value }) {
      const index = _.findIndex(this.filters, { id: parseInt(id) })

      let filter = this.filters[index]

      filter.queryOperator = value

      this.$set(this.filters, index, filter)

      this.toggleSettingsMode(index)

      this.dispatchUpdateQueryOperator({
        name: filter.name,
        queryOperator: value,
      })
    },

    /**
     * Updates the Multi select query operator
     *
     * @param   {string}  options.id
     * @param   {array}  options.value
     * @return  {void}
     */
    updateMultiSelectQueryOperator({ id, value }) {
      const index = _.findIndex(this.filters, { id: parseInt(id) })

      let filter = this.filters[index]

      filter.multiSelectQueryOperator = value

      this.$set(this.filters, index, filter)

      this.toggleSettingsMode(index)
    },
  },

  /**
   * Sets the beforeDestroy hook
   *
   * @return  {void}
   */
  beforeDestroy() {
    this.clearAdvancedSearchFilters()
  },

  /**
   * Set the watched properties
   *
   * @type  {Object}
   */
  watch: {
    augmentedFields(newEntry, oldEntry) {
      this.updateRefreshAddFilter()
    },
  },
}
